// The aligner in the desktop app's window of its own (brief 46). The app's initialization script
// marks the window, and then three things go through the app instead of the browser:
// - the menus: the system's menu bar while this window is key, from the model this page sends, with
//   the clicks coming back on the window's channel (docs/design/native-app.md, decisions 14 and 18);
// - saves: the app's save panel and atomic write instead of a download;
// - the autosave: two files in the app's data folder, `align/` under Application Support, instead
//   of the origin private file system.
// In a browser none of this exists and `desktop` is null.

import { Channel, invoke } from '@tauri-apps/api/core'
import { itemStates, menuShape, type MenuItem, type MenuModel } from '../../core/menu.ts'
import { decodeFrame } from '../../engine/frame.ts'
import { frameOf } from '../../engine/native.ts'
import type { AccountAnswer, AccountRequest, PageMessage } from '../../core/host.ts'

/// What the app says to this window: a menu item chosen, or the window became key and the items'
/// states are asked for again. The shell's other messages never come here.
type Heard = { kind: 'menu'; id: string } | { kind: 'validate' } | { kind: string }

/// What a save is for, which names the type the panel offers (apps/desktop/src/files.rs).
export type SaveKind = 'alignSession' | 'tmx'

/// The two files the autosave keeps; the app refuses any other name.
export type StoreOp = 'read' | 'write' | 'remove'

/// The app's Edit items for text, which the system draws and performs on the focused field, and
/// File's Close Window: a native window has them, and the page's own menus never did. Close Window
/// is the app's alone (apps/desktop/src/menu.rs), so the shell's own type of system item lacks it.
type System = NonNullable<Extract<MenuItem, { kind: 'command' }>['system']> | 'closeWindow'
const command = (id: string, label: string, system: System): MenuItem => ({ kind: 'command', id, label, shown: null, accelerator: null, enabled: true, checked: null, system: system as never })
const CLIPBOARD: MenuItem[] = [command('edit.cut', 'Cut', 'cut'), command('edit.copy', 'Copy', 'copy'), command('edit.paste', 'Paste', 'paste'), command('edit.selectAll', 'Select All', 'selectAll')]
const CLOSE: MenuItem = command('window.close', 'Close Window', 'closeWindow')

/// The model the app installs: the page's tree with the text items at the end of Edit and Close
/// Window at the end of File, each added where the menu is.
export function withSystemItems(model: MenuModel<string>): MenuModel<string> {
  const after = (items: MenuItem[], more: MenuItem[]) => (items.length ? [...items, { kind: 'separator' } as MenuItem, ...more] : more)
  const menus = model.menus.map(m => (m.id === 'edit' ? { ...m, items: after(m.items, CLIPBOARD) } : m.id === 'file' ? { ...m, items: after(m.items, [CLOSE]) } : m))
  for (const [id, label, items] of [['file', 'File', [CLOSE]], ['edit', 'Edit', CLIPBOARD]] as const) {
    if (!menus.some(m => m.id === id)) menus.splice(id === 'file' ? 1 : 2, 0, { id, label, items: [...items] })
  }
  return { ...model, menus }
}

/// The open screen's menus, before a session is open: the application menu, File's Close Window,
/// Edit's text items and Window, which the app fills.
export const OPENING: MenuModel<string> = withSystemItems({ generation: 0, menus: [{ id: 'app', label: 'Interplae', items: [] }, { id: 'window', label: 'Window', items: [] }] })

export class Desktop {
  private sent: { generation: number; shape: string } | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private readonly ready: Promise<void>
  /// The menus as they stand now, and what a chosen item runs; the editor sets both while a
  /// session is open, and the open screen's are the defaults.
  model: () => MenuModel<string> = () => OPENING
  run: (id: string) => void = () => {}

  constructor() {
    const channel = new Channel<Heard>()
    channel.onmessage = message => {
      if (message.kind === 'menu' && 'id' in message) this.run(message.id)
      else if (message.kind === 'validate') void this.push()
    }
    // The channel is the window's before anything is sent, so no click is missed.
    this.ready = invoke<void>('attach', { channel })
    void this.ready.then(() => this.push())
  }

  /// One push a frame's time at most after the page changed, as the shell's (src/app/shell.ts).
  schedule(): void {
    if (this.timer !== null) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.push()
    }, 16)
  }

  /// The tree to the app when its shape changed, under a new generation; else the items' states.
  async push(): Promise<void> {
    await this.ready
    const model = withSystemItems(this.model())
    const shape = menuShape(model as MenuModel)
    if (shape !== this.sent?.shape) {
      const generation = (this.sent?.generation ?? 0) + 1
      this.sent = { generation, shape }
      await invoke('page', { message: { kind: 'menu', model: { ...model, generation } } })
    } else await invoke('page', { message: { kind: 'menuStates', generation: this.sent.generation, states: itemStates(model as MenuModel) } })
  }

  /// The save panel, then the file written whole where the person chose. Answers the path, or null
  /// when the panel was cancelled.
  save(name: string, text: string, kind: SaveKind): Promise<string | null> {
    const bytes = new TextEncoder().encode(text)
    return invoke<string | null>('save', frameOf({ name, kind }, [['bytes', bytes.buffer as ArrayBuffer]]))
  }

  /// A request of the runtime's that needs no project: the Provider Keys' list and the review's
  /// sends with a key the runtime reads itself (brief 48).
  async engine<T>(request: Record<string, unknown>): Promise<T> {
    const answer = await invoke<ArrayBuffer>('engine', frameOf(request, []))
    const envelope = decodeFrame(answer).json as { ok: true; result: T } | { ok: false; error: { message: string } }
    if (!envelope.ok) throw new Error(envelope.error.message)
    return envelope.result
  }

  /// What the page tells the app, as the main window does.
  tell(message: PageMessage): Promise<unknown> {
    return invoke<unknown>('page', { message })
  }

  /// The organisations this device is signed in to (brief 49).
  account(request: AccountRequest): Promise<AccountAnswer> {
    return invoke<AccountAnswer>('account', { request })
  }

  /// One of the autosave's two files in the app's data folder: read answers its text or null.
  store(op: StoreOp, file: string, text?: string): Promise<string | null> {
    return invoke<string | null>('align_store', { op, file, text: text ?? null })
  }
}

/// The window's bridge, made once, or null in a browser.
export const desktop: Desktop | null = typeof window !== 'undefined' && (window as { __INTERPLAE_DESKTOP__?: { role?: string } }).__INTERPLAE_DESKTOP__?.role === 'align' ? new Desktop() : null
